import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { Subscription } from 'rxjs';

import { PromanButtonComponent } from '../button';
import { Fa6Module } from '../fa/fa6.module';
import { AiConversationMessage, CurrUser } from '../interfaces/object-interfaces';
import { AiFriendService } from '../services/ai-friend.service';
import { Entity } from '../services/entity.service';
import { PromanPaddingDirective } from '../shared/directives/proman-padding.directive';
import { PipesModule } from '../shared/pipes/pipes.module';
import { getCurrUser } from '../store/curr-user';
import { PromanTextSimpleComponent } from '../text-simple';
import { isDefinedNotNull } from '../utils';

interface AiConversationRow {
    id: number;
    createdBy?: { id: number; name: string };
    createdAt?: string;
    conversation?: AiConversationMessage[];
}

interface ChatMessage extends AiConversationMessage {
    rawMessage?: string;
}

@Component({
    selector: 'pro-ai-friend-conversations',
    imports: [
        FlexLayoutModule,
        Fa6Module,
        PipesModule,
        PromanPaddingDirective,
        PromanButtonComponent,
        PromanTextSimpleComponent,
    ],
    template: `
        <section class="ai-workspace" fxLayout="row" fxLayoutGap="16px">
            <main class="chat-panel" fxFlex>
                <header class="chat-header" fxLayout="row" fxLayoutAlign="space-between center">
                    <div class="chat-title-wrap" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
                        <button type="button" class="mobile-sidebar-toggle" (click)="toggleSidebarMobile()">
                            <fa name="bars"></fa>
                        </button>
                        <div class="chat-title">
                            <h2>{{ activeConversationTitle }}</h2>
                            @if (activeConversationOwner) {
                            <small>by {{ activeConversationOwner }}</small>
                            }
                        </div>
                    </div>
                    <pro-btn icon="plus" theme="accent" [label]="'new chat'" (onClick)="newConversation()"></pro-btn>
                </header>

                <div
                    #messageContainer
                    proPadding
                    class="message-container"
                    fxLayout="column"
                    fxLayoutGap="12px"
                    (scroll)="onMessageScroll()">
                    @if (!messageHistory.length) {
                    <div class="empty-state-wrap">
                        <div class="empty-state">{{ emptyStateMessage }}</div>
                    </div>
                    } @for (message of messageHistory; track $index) {
                    <article class="message-row" [class.user]="!!message.personId" [class.ai]="!message.personId">
                        <div class="message-bubble">
                            <div class="message-meta">
                                <small>{{ message.timestamp }}</small>
                                <button
                                    type="button"
                                    class="copy-btn"
                                    [class.visible]="!message.personId"
                                    (click)="copyMessage(message, $index)">
                                    <fa
                                        [name]="
                                            copiedMessageKey === messageKey(message, $index) ? 'check' : 'copy'
                                        "></fa>
                                </button>
                            </div>
                            <p proPadding [innerHTML]="message.message"></p>
                        </div>
                    </article>
                    } @if (isSending) {
                    <article class="message-row ai thinking-row">
                        <div class="message-bubble">
                            <div class="message-meta">
                                <small>processing</small>
                            </div>
                            <p proPadding>
                                Thinking<span class="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                            </p>
                        </div>
                    </article>
                    }
                </div>

                @if (showScrollToBottom) {
                <button type="button" class="scroll-bottom-btn" (click)="scrollToBottomAndHide()">
                    <fa name="arrow-down"></fa>
                </button>
                } @if (canWriteActiveConversation()) {
                <footer class="prompt-row" proPadding fxLayout="row" fxLayoutGap="10px">
                    <pro-text-simple
                        fxFlex
                        [value]="prompt"
                        [config]="{ label: 'message', type: 'textarea' }"
                        (onChange)="setPrompt($event)"
                        (keydown)="onPromptKeydown($event)"></pro-text-simple>
                    <pro-btn
                        icon="message-question"
                        theme="accent"
                        [disabled]="!prompt || isSending"
                        (onClick)="sendPrompt()"></pro-btn>
                </footer>
                <div class="chat-disclaimer" proPadding>SmartOn AI can make mistakes. Check important info.</div>
                }
            </main>

            <aside class="conversation-sidebar" [class.mobile-open]="isSidebarOpenMobile" fxLayout="column">
                <h3>Conversations</h3>
                <div class="conversation-list" fxLayout="column">
                    @for (conversation of conversations; track conversation.id) {
                    <button
                        type="button"
                        class="conversation-item"
                        [class.active]="conversation.id === selectedConversationId"
                        (click)="selectConversation(conversation.id)">
                        <div class="item-top">
                            <strong>#{{ conversation.id }}</strong>
                            <small>{{ formatDate(conversation.createdAt) }}</small>
                        </div>
                        <div class="item-preview">{{ conversationPreview(conversation) }}</div>
                    </button>
                    }
                </div>
            </aside>

            @if (isSidebarOpenMobile) {
            <button type="button" class="mobile-sidebar-backdrop" (click)="closeSidebarMobile()"></button>
            }
        </section>
    `,
    styles: `
        .ai-workspace {
            height: calc(100vh - 180px);
            min-height: 600px;
        }

        .chat-panel {
            background: #ffffff;
            border: 1px solid #d7dbe3;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            position: relative;
        }

        .chat-header {
            padding: 14px 18px;
            border-bottom: 1px solid #e6e9ef;
        }

        .chat-title h2 {
            margin: 0;
            font-size: 18px;
        }

        .chat-title-wrap {
            min-width: 0;
        }

        .chat-title small {
            color: #5f6b7a;
        }

        .mobile-sidebar-toggle {
            display: none;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            border: 1px solid #d5dbe6;
            background: #ffffff;
            color: #334155;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            flex-shrink: 0;
        }

        .message-container {
            flex: 1;
            overflow-y: auto;
            background: #f8fafc;
        }

        .empty-state {
            color: #6e7785;
            font-size: 18px;
            font-weight: 600;
        }

        .empty-state-wrap {
            flex: 1;
            min-height: 220px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        }

        .message-row {
            display: flex;
            width: 100%;
        }

        .message-row.ai {
            justify-content: flex-start;
        }

        .message-row.user {
            justify-content: flex-end;
        }

        .message-bubble {
            width: fit-content;
            max-width: 82%;
            background: #eef2f7;
            border-radius: 12px;
            border: 1px solid #d9e2ef;
            overflow: hidden;
        }

        .message-row.user .message-bubble {
            background: #dceeff;
            border-color: #b9d8fb;
        }

        .message-row p {
            margin: 0;
        }

        .message-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            padding: 6px 10px 0;
        }

        .message-meta small {
            font-size: 11px;
            color: #5f6b7a;
            line-height: 1.2;
        }

        .copy-btn {
            width: 24px;
            height: 24px;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: #4f5d70;
            cursor: pointer;
            opacity: 0;
            transition: opacity 0.12s ease, background 0.12s ease;
        }

        .copy-btn.visible,
        .message-row.user:hover .copy-btn {
            opacity: 1;
        }

        .copy-btn:hover {
            background: rgba(30, 58, 95, 0.12);
        }

        .thinking-row p {
            background: #f0f4fb;
            color: #2d3a4f;
            font-style: italic;
        }

        .thinking-dots span {
            animation: thinkingBlink 1.2s infinite ease-in-out;
            display: inline-block;
            width: 8px;
        }

        .thinking-dots span:nth-child(2) {
            animation-delay: 0.2s;
        }

        .thinking-dots span:nth-child(3) {
            animation-delay: 0.4s;
        }

        @keyframes thinkingBlink {
            0%, 80%, 100% {
                opacity: 0.2;
            }
            40% {
                opacity: 1;
            }
        }

        .prompt-row {
            border-top: 1px solid #e6e9ef;
            background: #ffffff;
        }

        .chat-disclaimer {
            border-top: 1px solid #edf1f6;
            background: #ffffff;
            color: #6b7280;
            font-size: 12px;
            line-height: 1.3;
            padding-top: 8px;
            padding-bottom: 10px;
        }

        .scroll-bottom-btn {
            position: absolute;
            right: 20px;
            bottom: 88px;
            width: 36px;
            height: 36px;
            border-radius: 999px;
            border: 1px solid #2e4e7a;
            background: #1d4b7d;
            color: #ffffff;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 5;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
        }

        .scroll-bottom-btn:hover {
            background: #245b97;
        }

        .conversation-sidebar {
            width: 320px;
            min-width: 320px;
            background: #0f172a;
            color: #e2e8f0;
            border-radius: 12px;
            padding: 14px;
            overflow: hidden;
        }

        .conversation-sidebar h3 {
            margin: 4px 0 12px;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #94a3b8;
        }

        .conversation-list {
            overflow-y: auto;
            gap: 8px;
        }

        .conversation-item {
            border: 1px solid #243247;
            border-radius: 10px;
            padding: 10px;
            background: #111c32;
            color: inherit;
            text-align: left;
            cursor: pointer;
            transition: background 0.15s ease, border-color 0.15s ease;
        }

        .conversation-item:hover {
            border-color: #3b4f6f;
            background: #172640;
        }

        .conversation-item.active {
            border-color: #7dd3fc;
            background: #1d2f4f;
        }

        .item-top {
            display: flex;
            justify-content: space-between;
            gap: 8px;
        }

        .item-top small {
            color: #94a3b8;
            font-size: 11px;
        }

        .item-preview {
            margin-top: 8px;
            font-size: 12px;
            color: #cbd5e1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .mobile-sidebar-backdrop {
            display: none;
        }

        :host ::ng-deep .chat-md-table-wrap {
            overflow-x: auto;
            margin: 8px 0;
        }

        :host ::ng-deep .chat-md-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            background: #fff;
        }

        :host ::ng-deep .chat-md-table th,
        :host ::ng-deep .chat-md-table td {
            border: 1px solid #d8dee8;
            padding: 6px 8px;
            text-align: left;
            vertical-align: top;
        }

        :host ::ng-deep .chat-md-table th {
            background: #f3f6fb;
            font-weight: 600;
        }

        @media (max-width: 960px) {
            .ai-workspace {
                min-height: calc(100vh - 140px);
                gap: 0;
            }

            .mobile-sidebar-toggle {
                display: inline-flex;
            }

            .conversation-sidebar {
                position: fixed;
                top: 0;
                right: -340px;
                width: 300px;
                min-width: 300px;
                height: 100vh;
                border-radius: 0;
                z-index: 30;
                transition: right 0.2s ease;
            }

            .conversation-sidebar.mobile-open {
                right: 0;
            }

            .mobile-sidebar-backdrop {
                display: block;
                position: fixed;
                inset: 0;
                border: 0;
                background: rgba(2, 6, 23, 0.45);
                z-index: 20;
            }
        }
    `,
})
export class AiFriendConversationsComponent implements OnInit, OnDestroy {
    private AiFriend = inject(AiFriendService);
    private Entity = inject(Entity);
    private store = inject(Store);

    @ViewChild('messageContainer') private messageContainer?: ElementRef<HTMLElement>;

    currUser: CurrUser;
    prompt = '';
    messageHistory: ChatMessage[] = [];
    conversations: AiConversationRow[] = [];
    selectedConversationId: number = -1;
    activeConversationOwner: string = '';
    activeConversationTitle = 'New conversation';
    emptyStateMessage = 'How can I help?';
    isSending = false;
    showScrollToBottom = false;
    isSidebarOpenMobile = false;
    copiedMessageKey: string | null = null;
    private lastSubmittedPrompt = '';
    private ignorePromptEchoUntil = 0;
    private copiedMessageTimeout: ReturnType<typeof setTimeout>;
    private readonly emptyStateVariants = [
        'How can I help?',
        'What\u2019s on your mind today?',
        'What would you like to do next?',
        'Need help with something in SmartOn?',
        'What can I assist you with right now?',
    ];

    private subscriptions = new Subscription();

    ngOnInit() {
        this.subscriptions.add(
            this.store.select(getCurrUser).subscribe((value) => {
                this.currUser = value;
                if (!this.currUser?.person?.id) return;
                if (this.selectedConversationId <= 0) this.newConversation();
                this.loadConversations();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.copiedMessageTimeout) clearTimeout(this.copiedMessageTimeout);
    }

    setPrompt = (value: string) => {
        const nextValue = value || '';
        const now = Date.now();
        const isStaleEcho =
            now < this.ignorePromptEchoUntil &&
            nextValue === this.lastSubmittedPrompt &&
            (this.prompt === '' || this.isSending);

        if (isStaleEcho) return;
        this.prompt = nextValue;
    };

    onPromptKeydown(event: KeyboardEvent) {
        const isEnter = event.key === 'Enter';
        const hasCommand = event.metaKey || event.ctrlKey;
        if (!isEnter || !hasCommand) return;
        event.preventDefault();
        if (!this.prompt || this.isSending) return;
        this.sendPrompt();
    }

    newConversation = () => {
        this.selectedConversationId = -1;
        this.AiFriend.selectedConversation.set(-1);
        this.AiFriend.authorId = this.currUser?.person?.id;
        this.messageHistory = [];
        this.emptyStateMessage = this.pickRandomEmptyStateMessage();
        this.activeConversationTitle = 'New conversation';
        this.activeConversationOwner = this.currUser?.person?.name || '';
        this.scheduleScrollToBottom(true);
    };

    canWriteActiveConversation = () => this.AiFriend.authorId === this.currUser?.person?.id;

    selectConversation = (id: number) => {
        this.selectedConversationId = id;
        this.AiFriend.selectedConversation.set(id);
        this.loadConversation(id);
        this.closeSidebarMobile();
    };

    toggleSidebarMobile = () => {
        this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
    };

    closeSidebarMobile = () => {
        this.isSidebarOpenMobile = false;
    };

    sendPrompt() {
        if (!isDefinedNotNull(this.prompt) || this.isSending) return;

        const currentPrompt = this.prompt;
        this.lastSubmittedPrompt = currentPrompt;
        this.ignorePromptEchoUntil = Date.now() + 2500;
        this.messageHistory.push({
            personId: this.currUser.person.id,
            message: this.formatMessage(currentPrompt),
            rawMessage: currentPrompt,
            timestamp: this.formatTimestamp(moment().unix()),
        });
        this.scheduleScrollToBottom(true);
        this.prompt = '';
        this.isSending = true;
        this.scheduleScrollToBottom(true);

        const conversationId = this.selectedConversationId > 0 ? this.selectedConversationId : undefined;
        this.AiFriend.askFriend(currentPrompt, conversationId)
            .then((result: { answer: string; id: number }) => {
                if (this.selectedConversationId <= 0) {
                    this.selectedConversationId = result.id;
                    this.AiFriend.selectedConversation.set(result.id);
                    this.activeConversationTitle = `Conversation #${result.id}`;
                }
                this.messageHistory.push({
                    personId: 0,
                    message: this.formatMessage(result.answer),
                    rawMessage: result.answer,
                    timestamp: this.formatTimestamp(moment().unix()),
                });
                this.scheduleScrollToBottom();
                this.loadConversations();
            })
            .finally(() => {
                this.isSending = false;
                this.scheduleScrollToBottom();
            });
    }

    deleteConversation = (id: number) => {
        this.Entity.get('ai_conversation')
            .remove({ id })
            .then(() => {
                if (this.selectedConversationId === id) this.newConversation();

                this.loadConversations();
            });
    };

    conversationPreview = (conversation: AiConversationRow) => {
        const history = conversation.conversation || [];
        const last = history.length ? history[history.length - 1] : null;
        const message = last?.message || '';
        return message.replace(/\n/g, ' ').slice(0, 90) || 'No messages yet';
    };

    formatDate = (value: string) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '');

    private loadConversations() {
        if (!this.currUser?.person?.id) return;
        this.Entity.get('ai_conversation')
            .search({
                limit: 100,
                offset: 0,
                sort: { createdAt: 'desc' },
                partialJoin: { createdBy: ['id', 'name'] },
                'createdBy.id': this.currUser.person.id,
            })
            .then((response: any) => {
                this.conversations = this.extractRows(response);
                if (this.selectedConversationId > 0) {
                    const stillExists = this.conversations.some((row) => row.id === this.selectedConversationId);
                    if (!stillExists) this.newConversation();
                }
            });
    }

    private loadConversation(id: number) {
        this.Entity.get('ai_conversation')
            .get({ id, partialJoin: { createdBy: ['id', 'name'] } })
            .then((conversation: any) => {
                if (!conversation) return;
                this.AiFriend.authorId = conversation.createdBy?.id;
                this.activeConversationOwner = conversation.createdBy?.name || '';
                this.activeConversationTitle = `Conversation #${conversation.id}`;
                this.messageHistory = (conversation.conversation || []).map((msg: AiConversationMessage) => ({
                    ...msg,
                    message: this.formatMessage(msg.message),
                    rawMessage: msg.message,
                    timestamp: this.formatTimestamp(msg.timestamp),
                }));
                this.scheduleScrollToBottom(true);
            });
    }

    messageKey(message: ChatMessage, index: number) {
        return `${index}-${message.personId || 0}`;
    }

    async copyMessage(message: ChatMessage, index: number) {
        const text = (message.rawMessage || this.htmlToPlainText(message.message || '')).trim();
        if (!text) return;
        const key = this.messageKey(message, index);
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        this.copiedMessageKey = key;
        if (this.copiedMessageTimeout) clearTimeout(this.copiedMessageTimeout);
        this.copiedMessageTimeout = setTimeout(() => {
            this.copiedMessageKey = null;
        }, 1200);
    }

    private extractRows(response: any): AiConversationRow[] {
        if (!response) return [];
        if (Array.isArray(response.rows)) return response.rows;
        if (Array.isArray(response.data)) return response.data;
        if (Array.isArray(response.items)) return response.items;
        if (Array.isArray(response)) return response;
        return [];
    }

    private formatTimestamp(timestamp: string | number) {
        if (!timestamp) return '';
        const unixValue = Number(timestamp);
        if (!Number.isNaN(unixValue) && unixValue > 0) return moment.unix(unixValue).format('YYYY-MM-DD HH:mm:ss');

        return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
    }

    private formatMessage(value: string) {
        if (!value) return '';
        const normalized = value.replace(/\r\n/g, '\n');
        const lines = normalized.split('\n');
        const htmlParts: string[] = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const next = lines[i + 1];

            if (this.isMarkdownTableHeader(line, next)) {
                const headerCells = this.parseTableRow(line);
                const rows: string[][] = [];
                i += 2;
                while (i < lines.length && this.looksLikeTableRow(lines[i])) {
                    rows.push(this.parseTableRow(lines[i]));
                    i++;
                }
                i -= 1;
                htmlParts.push(this.renderTable(headerCells, rows));
                continue;
            }

            htmlParts.push(this.formatInline(line));
        }

        return htmlParts.join('<br>');
    }

    private isMarkdownTableHeader(line: string, separator?: string) {
        if (!line || !separator) return false;
        if (!this.looksLikeTableRow(line)) return false;
        return /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(separator);
    }

    private looksLikeTableRow(line: string) {
        return !!line && line.includes('|');
    }

    private parseTableRow(line: string): string[] {
        const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
        return trimmed.split('|').map((cell) => cell.trim());
    }

    private renderTable(headers: string[], rows: string[][]): string {
        const th = headers.map((cell) => `<th>${this.formatInline(cell)}</th>`).join('');
        const bodyRows = rows
            .map((cells) => `<tr>${cells.map((cell) => `<td>${this.formatInline(cell)}</td>`).join('')}</tr>`)
            .join('');
        return `<div class="chat-md-table-wrap"><table class="chat-md-table"><thead><tr>${th}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
    }

    private formatInline(value: string): string {
        const escaped = this.escapeHtml(value || '');
        const withMarkdownLinks = escaped
            .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
            .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

        const withBracketLinks = withMarkdownLinks.replace(
            /&lt;(https?:\/\/[^&\s]+)&gt;/g,
            '<a href="$1" target="_blank" rel="noopener">$1</a>'
        );

        return withBracketLinks.replace(/(^|[\s(>])((https?:\/\/|www\.)[^\s<)&]+)/g, (match, prefix, url) => {
            if (/<a\s/i.test(match)) return match;
            const href = url.startsWith('http') ? url : `https://${url}`;
            return `${prefix}<a href="${href}" target="_blank" rel="noopener">${url}</a>`;
        });
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private htmlToPlainText(value: string): string {
        const tmp = document.createElement('div');
        tmp.innerHTML = value || '';
        return tmp.textContent || tmp.innerText || '';
    }

    private pickRandomEmptyStateMessage(): string {
        const index = Math.floor(Math.random() * this.emptyStateVariants.length);
        return this.emptyStateVariants[index];
    }

    onMessageScroll() {
        const el = this.messageContainer?.nativeElement;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        this.showScrollToBottom = distanceFromBottom > 80;
    }

    scrollToBottomAndHide() {
        this.scrollToBottom(true);
    }

    private scheduleScrollToBottom(force = false) {
        setTimeout(() => this.scrollToBottom(force), 0);
    }

    private scrollToBottom(force = false) {
        const el = this.messageContainer?.nativeElement;
        if (!el) return;
        if (!force) {
            const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
            if (distanceFromBottom > 80) return;
        }
        el.scrollTop = el.scrollHeight;
        this.showScrollToBottom = false;
    }
}
